import React, { useState, useEffect, useCallback, useRef } from "react";
import { Note } from "../../../soundLibrary";
import { useSoundLibrary } from "../../../hooks/useSoundLibrary";
import { useDrillLocalStorage } from "../../../hooks/useDrillLocalStorage";
import { usePlaybackSettings } from "../../../hooks/usePlaybackSettings";
import { generateRandomMelody, generateIncorrectSequences } from "../../../utils/musicTheory";
import MelodyQuiz, { MelodyQuizProps, MelodyQuizOptions } from "./MelodyQuiz";
import MelodyPreQuiz from "./MelodyPreQuiz";
import PlaybackOptionsPanel from "../../../components/PlaybackOptionsPanel";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import { ROUTES } from "../../../config/routes";
import { SEO_METADATA } from "../../../config/seoMetadata";
import SEO from "../../../components/SEO";
import styles from "../../../styles/MelodyQuiz.module.css";

// Use a fixed reference note for the entire quiz
const REFERENCE_NOTE: Note = "C4" as Note;

const defaultMelodyOptions: MelodyQuizOptions = {
    selectedScales: ["MAJOR"],
    selectedDegrees: [1, 2, 3, 5],
    melodyLength: 6,
    mode: "questions",
    timeLimitSeconds: 60,
    difficulty: 1,
    referenceNote: REFERENCE_NOTE,
};

export default function MelodyQuizWrapper() {
    const { playSequence, playNote, stopAll } = useSoundLibrary();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const isPlayingRef = useRef(false);
    const isMountedRef = useRef(true);

    // Persist quiz options to localStorage
    const [quizOptions, setQuizOptions] = useDrillLocalStorage<MelodyQuizOptions>(
        "drill_melody_storage",
        defaultMelodyOptions
    );

    // Persist playback settings to localStorage (shared across all drills)
    const [playbackOptions, setPlaybackOptions] = usePlaybackSettings();

    const [quizStarted, setQuizStarted] = useState(false);

    // Question state
    const [currentQuestion, setCurrentQuestion] = useState<MelodyQuizProps["currentQuestion"]>(null);
    const [questionCount, setQuestionCount] = useState(0);
    const [score, setScore] = useState(0);
    const [attemptedQuestions, setAttemptedQuestions] = useState(0);

    // Timer state
    const [timeLeft, setTimeLeft] = useState(quizOptions.timeLimitSeconds || 0);

    // UI state
    const [feedback, setFeedback] = useState<{ message: string; color: string } | null>(null);
    const [quizEnded, setQuizEnded] = useState(false);

    useEffect(() => {
        if (quizOptions.mode === "time" && timeLeft > 0 && quizStarted) {
            const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [quizOptions.mode, timeLeft, quizStarted]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            stopAll(); // Stop any playing sounds when unmounting
        };
    }, [stopAll]);

    const disableStart = () => {
        return quizOptions.selectedScales.length === 0 || quizOptions.selectedDegrees.length === 0;
    };

    const generateQuestion = useCallback(() => {

        const { selectedScales, selectedDegrees, melodyLength, difficulty = 1, referenceNote = REFERENCE_NOTE } = quizOptions;

        if (selectedScales.length === 0 || selectedDegrees.length === 0) {
            alert("Please select at least one scale type and scale degree");
            return;
        }

        // Pick random scale type
        const correctScale = selectedScales[Math.floor(Math.random() * selectedScales.length)];

        // Generate melody with correct scale degree sequence
        const { melody, sequence: correctSequence } = generateRandomMelody(
            referenceNote,
            correctScale,
            selectedDegrees,
            melodyLength
        );

        // Generate 4 incorrect sequences (always 5 total options: 1 correct + 4 incorrect)
        const incorrectSequences = generateIncorrectSequences(
            correctSequence,
            selectedDegrees,
            4,
            melodyLength,
            difficulty
        );

        // Create question options (shuffle them)
        const questionOptions = [correctSequence, ...incorrectSequences].sort(
            () => Math.random() - 0.5
        );

        setCurrentQuestion({
            melody,
            correctSequence,
            questionOptions,
            referenceNote,
            scaleType: correctScale,
        });
    }, [quizOptions]);

    const playReferenceNote = useCallback(() => {
        const referenceNote = quizOptions.referenceNote || REFERENCE_NOTE;
        const activeVolume = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
        playNote(playbackOptions.instrument, referenceNote, { velocity: activeVolume });
    }, [playNote, playbackOptions, quizOptions.referenceNote]);

    const resetQuiz = useCallback(() => {
        setCurrentQuestion(null);
        setQuestionCount(0);
        setScore(0);
        setAttemptedQuestions(0);
        setFeedback(null);
        setQuizEnded(false);
        setQuizStarted(false);
        
        // Scroll to top when resetting quiz
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    useEffect(() => {
        if (quizEnded) {
            // Scroll to top of page when quiz ends
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizEnded]);

    useEffect(() => {
        if (quizStarted) {
            // Scroll to top of page when quiz starts
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizStarted]);

    const startQuiz = useCallback(() => {
        setQuizStarted(true);
        setQuestionCount(0);
        setScore(0);
        setAttemptedQuestions(0);
        setTimeLeft(quizOptions.timeLimitSeconds || 0);
        setFeedback(null);
        setQuizEnded(false);

        setTimeout(() => generateQuestion(), 100);
    }, [generateQuestion, quizOptions.timeLimitSeconds]);

    const endQuiz = useCallback(() => {
        stopAll(); // Stop any currently playing melody
        setQuizEnded(true);
    }, [stopAll]);

    const playCurrentMelody = useCallback(async () => {
        if (!currentQuestion || isPlayingRef.current) return;

        isPlayingRef.current = true;
        try {
            const activeVolume = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
            await playSequence(
                playbackOptions.instrument,
                currentQuestion.melody,
                {
                    velocity: activeVolume,
                    duration: 0.5, // 500ms per note
                }
            );
        } catch (error) {
            if (isMountedRef.current) {
                console.error("Error playing melody:", error);
                setFeedback({
                    message: "Error playing melody. Try again.",
                    color: "#ef4444",
                });
            }
        } finally {
            isPlayingRef.current = false;
        }
    }, [currentQuestion, playbackOptions, playSequence, setFeedback]);

    useEffect(() => {
        if (currentQuestion && quizStarted && !quizEnded) {
            const timer = setTimeout(() => {
                playCurrentMelody();
            }, 300);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion, quizStarted, quizEnded]);

    return (
        <>
            <SEO {...SEO_METADATA.DRILL_MELODIES} />
            <div ref={wrapperRef} className={styles.melodyDrillWrapper}>
            <div className={styles.melodyDrillHeader}>
                <div className={styles.titleSection}>Melodies Drill</div>

                {!quizStarted && (
                    <div className={styles.headerTop}>
                        <button
                            onClick={() => startQuiz()}
                            disabled={disableStart()}
                            className={styles.startQuizButton}
                        >
                            Start Quiz
                        </button>
                    </div>
                )}

                {quizStarted && !quizEnded && (
                    <div className={styles.quizControlsPanel}>
                        {(quizOptions.mode !== "endless") && (
                            <div className={styles.quizStatus}>
                                {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                                {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                                 questionCount <= quizOptions.questionLimit && (
                                    <div className={styles.statusItem}>📝 {questionCount + 1}/{quizOptions.questionLimit}</div>
                                )}
                            </div>
                        )}
                        <div className={styles.quizActions}>
                            {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                              (quizOptions.mode === "time" && timeLeft > 0) ||
                              (quizOptions.mode === "endless" && !quizEnded)) && (
                                <button className={styles.playButton} onClick={playCurrentMelody}>
                                    Play
                                </button>
                            )}
                            <button onClick={endQuiz} className={styles.endQuizButton}>
                                End Quiz
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.quizAndPlaybackOpts}>
                {quizStarted ? (
                    <MelodyQuiz
                        {...quizOptions}
                        playbackOptions={playbackOptions}
                        currentQuestion={currentQuestion}
                        setCurrentQuestion={setCurrentQuestion}
                        questionCount={questionCount}
                        setQuestionCount={setQuestionCount}
                        score={score}
                        setScore={setScore}
                        attemptedQuestions={attemptedQuestions}
                        setAttemptedQuestions={setAttemptedQuestions}
                        timeLeft={timeLeft}
                        setTimeLeft={setTimeLeft}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        generateQuestion={generateQuestion}
                        quizEnded={quizEnded}
                        setQuizEnded={setQuizEnded}
                        resetQuiz={resetQuiz}
                        playReferenceNote={playReferenceNote}
                        stopAll={stopAll}
                    />
                ) : (
                    <div className={styles.preQuizWithBanner}>
                        <LearnMoreBanner
                            to={ROUTES.LEARN.MELODIES}
                            variant="intro"
                            introTitle="What are scale degrees?"
                            introText={
                                <>
                                    <strong>Scale degrees</strong> (1-7) describe a note's position in a scale,
                                    not its absolute pitch—a melody that goes 1-3-5 sounds the same in any key.
                                    Hearing in scale degrees makes transcribing songs dramatically faster
                                    because you're recognizing patterns, not just individual notes.
                                </>
                            }
                            examplesLabel="Tips:"
                            examples={[
                                { primary: "Start simple", secondary: "Try just 1, 3, 5" },
                                { primary: "Use your anchor", secondary: "Sing '1' first" },
                                { primary: "Build up", secondary: "Add degrees gradually" },
                            ]}
                        />
                        <MelodyPreQuiz setQuizOptions={setQuizOptions} initialOptions={quizOptions} />
                    </div>
                )}

                <PlaybackOptionsPanel
                    onChange={setPlaybackOptions}
                    initialOptions={playbackOptions}
                    forcePlayMode="melodic"
                />
            </div>

            {!quizStarted && (
                <div className={styles.startQuizFooter}>
                    <button
                        onClick={() => startQuiz()}
                        disabled={disableStart()}
                        className={styles.startQuizButton}
                    >
                        Start Quiz
                    </button>
                </div>
            )}

            {quizStarted && !quizEnded && (
                <div className={styles.quizControlsFooter}>
                    {(quizOptions.mode !== "endless") && (
                        <div className={styles.quizStatus}>
                            {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                            {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                             questionCount <= quizOptions.questionLimit && (
                                <div className={styles.statusItem}>📝 {questionCount + 1}/{quizOptions.questionLimit}</div>
                            )}
                        </div>
                    )}
                    <div className={styles.quizActions}>
                        {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                          (quizOptions.mode === "time" && timeLeft > 0) ||
                          (quizOptions.mode === "endless" && !quizEnded)) && (
                            <button className={styles.playButton} onClick={playCurrentMelody}>
                                Play
                            </button>
                        )}
                        <button onClick={endQuiz} className={styles.endQuizButton}>
                            End Quiz
                        </button>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}
